import { create } from 'zustand';
import { db, withSyncMeta } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import type { Code99Event, Code99EventType, Code99Session } from '@/types';

/**
 * Code 99 resuscitation state.
 *
 * One session per ePCR. The store loads (or creates) the session, holds
 * the live event list, and exposes the few mutating verbs used by the
 * workspace: `logEvent`, `updateSession`, `closeSession`. Every write
 * lands in Dexie immediately (offline-first) and is enqueued for sync
 * to Supabase when the network is up.
 */

interface ActivateArgs {
  agencyId: string;
  incidentId: string;
  epcrId: string;
  userId: string;
  reason?: string;
}

interface LogEventArgs {
  eventType: Code99EventType;
  eventTime?: string;                 // defaults to now()
  details?: Record<string, unknown>;
  performedBy?: string;
  enteredBy?: string;
  retroactive?: boolean;
}

interface Code99State {
  session: Code99Session | null;
  events: Code99Event[];
  loading: boolean;

  loadByEpcr: (epcrId: string) => Promise<void>;
  activate: (args: ActivateArgs) => Promise<Code99Session>;
  logEvent: (args: LogEventArgs) => Promise<Code99Event | null>;
  updateSession: (patch: Partial<Code99Session>) => Promise<void>;
  clear: () => void;
}

export const useCode99Store = create<Code99State>((set, get) => ({
  session: null,
  events: [],
  loading: false,

  async loadByEpcr(epcrId) {
    set({ loading: true });
    try {
      const session = await db.code99_sessions.where('epcr_id').equals(epcrId).first();
      if (!session) {
        set({ session: null, events: [] });
        return;
      }
      const events = await db.code99_events
        .where('session_id').equals(session.id)
        .sortBy('event_time');
      // newest first in memory so render order matches design
      events.reverse();
      set({ session, events });
    } finally {
      set({ loading: false });
    }
  },

  async activate({ agencyId, incidentId, epcrId, userId, reason }) {
    const existing = await db.code99_sessions.where('epcr_id').equals(epcrId).first();
    if (existing) {
      await get().loadByEpcr(epcrId);
      return existing;
    }
    const now = new Date().toISOString();
    const session: Code99Session = {
      id: crypto.randomUUID(),
      agency_id: agencyId,
      incident_id: incidentId,
      epcr_id: epcrId,
      activated_at: now,
      activated_by: userId,
      activated_reason: reason ?? 'manual',
      closed_at: null,
      closed_by: null,
      arrest_time: null,
      arrest_time_source: null,
      cpr_started_at: null,
      disposition: null,
      med_cmd_contacted: null,
      med_cmd_physician_name: null,
      med_cmd_physician_badge: null,
      med_cmd_contact_time: null,
      time_of_death: null,
      family_notified: null,
      law_enforcement_on_scene: null,
      post_rosc_12_lead_at: null,
      post_rosc_vitals_captured_at: null,
      post_rosc_airway_confirmed_at: null,
      narrative_generated_at: null,
      narrative_approved_at: null,
      narrative_approved_by: null,
      compliance_check: null,
      created_at: now,
      updated_at: now,
    };
    await db.code99_sessions.add(withSyncMeta(session));
    await enqueueSync('code99_sessions', session.id, 'insert', session as unknown as Record<string, unknown>);
    set({ session, events: [] });
    return session;
  },

  async logEvent({ eventType, eventTime, details, performedBy, enteredBy, retroactive }) {
    const s = get().session;
    if (!s) return null;
    const ev: Code99Event = {
      id: crypto.randomUUID(),
      session_id: s.id,
      epcr_id: s.epcr_id,
      agency_id: s.agency_id,
      event_type: eventType,
      event_time: eventTime ?? new Date().toISOString(),
      retroactive: !!retroactive,
      details: details ?? {},
      performed_by: performedBy ?? null,
      entered_by: enteredBy ?? null,
      created_at: new Date().toISOString(),
    };
    await db.code99_events.add(withSyncMeta(ev));
    await enqueueSync('code99_events', ev.id, 'insert', ev as unknown as Record<string, unknown>);

    // Derive convenience session fields on certain events
    let sessionPatch: Partial<Code99Session> | null = null;
    if (eventType === 'cpr_start' && !s.cpr_started_at) {
      sessionPatch = { cpr_started_at: ev.event_time };
    } else if (eventType === 'rosc' && !s.disposition) {
      sessionPatch = { disposition: 'rosc' };
    }
    if (sessionPatch) await get().updateSession(sessionPatch);

    set((st) => ({ events: [ev, ...st.events] }));
    return ev;
  },

  async updateSession(patch) {
    const s = get().session;
    if (!s) return;
    const now = new Date().toISOString();
    const updated: Code99Session = { ...s, ...patch, updated_at: now };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await db.code99_sessions.update(s.id, { ...patch, updated_at: now, _dirty: true, _last_modified: now } as any);
    await enqueueSync('code99_sessions', s.id, 'update', updated as unknown as Record<string, unknown>);
    set({ session: updated });
  },

  clear() {
    set({ session: null, events: [] });
  },
}));
